import { useLayoutEffect, useMemo, useRef, useState } from "react";
import type { UseProgressResult } from "@/core/hooks/useProgress";
import { flattenLessons, modulesByLevel, type Course, type CourseModule } from "@/core/types";
import {
  findNextLesson,
  formatMinutes,
  isLessonDone,
  lessonStepsDone,
  moduleMinutes,
} from "@/ui/progressStats";
import { useRollingNumber } from "@/ui/useRollingNumber";
import { Icon } from "./Icon";
import { CurriculumBand, StatusRing, StepTrack, moduleSteps } from "./Progress";

interface CourseOverviewProps {
  course: Course;
  progress: UseProgressResult;
  onOpenLesson(moduleId: string, lessonId: string): void;
}

/** Wie viele Lektionen eines Moduls sind vollständig abgeschlossen? */
function completedLessons(module: CourseModule, progress: UseProgressResult): number {
  return module.lessons.filter((lesson) => isLessonDone(module, lesson, progress)).length;
}

export function CourseOverview({ course, progress, onOpenLesson }: CourseOverviewProps) {
  const groups = useMemo(() => modulesByLevel(course), [course]);
  const lessons = useMemo(() => flattenLessons(course), [course]);
  const moduleRefs = useRef(new Map<string, HTMLLIElement>());

  const next = findNextLesson(course, progress, progress.progress.lastLessonKey);
  const lessonsDone = lessons.filter(({ module, lesson }) =>
    isLessonDone(module, lesson, progress),
  ).length;
  const percentage = useRollingNumber(progress.percentage);

  // Bei einem Kurs mit vielen Modulen ist höchstens ein Modul aufgeklappt.
  // Voreinstellung: das Modul der nächsten Lektion – bestimmt, sobald der
  // gespeicherte Fortschritt geladen ist. Danach entscheidet die lernende Person.
  const defaultModuleId = next?.module.id ?? course.modules[0]?.id ?? null;
  const [openModuleId, setOpenModuleId] = useState<string | null>(
    progress.loaded ? defaultModuleId : null,
  );
  const chosen = useRef(progress.loaded);
  useLayoutEffect(() => {
    if (chosen.current || !progress.loaded) return;
    chosen.current = true;
    setOpenModuleId(defaultModuleId);
  }, [progress.loaded, defaultModuleId]);

  const selectModule = (moduleId: string) => {
    setOpenModuleId(moduleId);
    // Erst nach dem Aufklappen scrollen, damit das Ziel an der richtigen Stelle steht.
    requestAnimationFrame(() => {
      moduleRefs.current.get(moduleId)?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const nextStepsDone = next ? lessonStepsDone(next.module, next.lesson, progress) : 0;
  const nextModuleNumber = next ? course.modules.indexOf(next.module) + 1 : 0;
  const nextLabel =
    progress.completedCount === 0
      ? "Kurs beginnen"
      : nextStepsDone > 0
        ? "Lektion fortsetzen"
        : "Lektion starten";

  return (
    <div className="overview">
      <header className="title-block">
        <h1>{course.title}</h1>
        <p className="title-block__sub">{course.summary}</p>
      </header>

      <section className="panel hero-panel" aria-labelledby="fortschritt-titel">
        <div className="hero-panel__head">
          <h2 id="fortschritt-titel" className="panel-title">
            Fortschritt
          </h2>
          <span className="meta">{course.modules.length} Module</span>
        </div>

        <div className="figures">
          <p className="figure">
            <span className="figure__value num">{percentage}</span>
            <span className="figure__unit">%</span>
          </p>
          <dl className="figures__counts">
            <div>
              <dt>Schritte</dt>
              <dd className="num">
                {progress.completedCount} <span>von {progress.totalCount}</span>
              </dd>
            </div>
            <div>
              <dt>Lektionen</dt>
              <dd className="num">
                {lessonsDone} <span>von {lessons.length}</span>
              </dd>
            </div>
          </dl>
        </div>

        <CurriculumBand course={course} progress={progress} onSelectModule={selectModule} />

        <div className="next">
          {next ? (
            <>
              <h3 className="next__label">Als Nächstes</h3>
              <p className="next__title">{next.lesson.title}</p>
              <p className="next__meta">
                Modul {nextModuleNumber} · {next.module.title} ·{" "}
                <span className="nowrap">{next.lesson.durationMinutes} Min.</span>
              </p>
              <div className="next__track">
                <StepTrack
                  steps={next.lesson.steps.map((step) => ({
                    id: step.id,
                    title: step.title,
                    done: progress.isStepDone(next.module.id, next.lesson.id, step.id),
                  }))}
                  current={-1}
                />
                <span className="next__count num">
                  {nextStepsDone}/{next.lesson.steps.length}
                </span>
              </div>
              <button
                type="button"
                className="btn btn--primary btn--block"
                onClick={() => onOpenLesson(next.module.id, next.lesson.id)}
              >
                {nextLabel}
                <Icon name="arrowRight" />
              </button>
            </>
          ) : (
            <>
              <h3 className="next__label">Kurs abgeschlossen</h3>
              <p className="next__title">Alle {lessons.length} Lektionen sind erledigt.</p>
              <p className="next__meta">
                Jede Lektion lässt sich weiter öffnen und erneut durcharbeiten.
              </p>
            </>
          )}
        </div>
      </section>

      {groups.map((group) => {
        const minutes = group.modules.reduce(
          (sum, module) =>
            sum + module.lessons.reduce((inner, lesson) => inner + lesson.durationMinutes, 0),
          0,
        );
        return (
          <section key={group.level} className="module-group" aria-labelledby={`stufe-${group.level}`}>
            <header className="section-head">
              <h2 id={`stufe-${group.level}`}>{group.level}</h2>
              <span className="meta">
                {group.modules.length} Module · {formatMinutes(minutes)}
              </span>
            </header>

            <ul className="panel module-list">
              {group.modules.map((module) => {
                const done = completedLessons(module, progress);
                const steps = moduleSteps(module, progress);
                const isOpen = openModuleId === module.id;
                const minutesModule = moduleMinutes(module);

                return (
                  <li
                    key={module.id}
                    ref={(element) => {
                      if (element) moduleRefs.current.set(module.id, element);
                      else moduleRefs.current.delete(module.id);
                    }}
                    className={`module ${isOpen ? "is-open" : ""}`}
                  >
                    <button
                      type="button"
                      className="module__header"
                      onClick={() => setOpenModuleId(isOpen ? null : module.id)}
                      aria-expanded={isOpen}
                    >
                      <StatusRing fraction={steps.total === 0 ? 0 : steps.done / steps.total} />
                      <span className="module__text">
                        <span className="module__title">{module.title}</span>
                        <span className="module__meta num">
                          {done === module.lessons.length
                            ? `Abgeschlossen · ${module.lessons.length} Lektionen`
                            : `${done} von ${module.lessons.length} Lektionen · ${formatMinutes(minutesModule)}`}
                        </span>
                      </span>
                      <Icon name="chevronDown" className="module__chevron" />
                    </button>

                    {isOpen ? (
                      <div className="module__body">
                        <p className="module__summary">{module.summary}</p>
                        <ol className="lesson-list">
                          {module.lessons.map((lesson) => {
                            const doneSteps = lessonStepsDone(module, lesson, progress);
                            const total = lesson.steps.length;
                            const isNext =
                              next?.lesson.id === lesson.id && next.module.id === module.id;

                            return (
                              <li key={lesson.id}>
                                <button
                                  type="button"
                                  className={`lesson-row ${isNext ? "is-next" : ""}`}
                                  onClick={() => onOpenLesson(module.id, lesson.id)}
                                >
                                  <StatusRing fraction={doneSteps / total} size={16} />
                                  <span className="lesson-row__text">
                                    <span className="lesson-row__title">{lesson.title}</span>
                                    <span className="lesson-row__meta num">
                                      {doneSteps === total
                                        ? "Abgeschlossen"
                                        : doneSteps > 0
                                          ? `${doneSteps} von ${total} Schritten`
                                          : `${total} Schritte`}{" "}
                                      · {lesson.durationMinutes} Min.
                                    </span>
                                  </span>
                                  <Icon name="chevronRight" className="lesson-row__chevron" />
                                </button>
                              </li>
                            );
                          })}
                        </ol>
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}

      {progress.completedCount > 0 ? (
        <footer className="overview__footer">
          <button
            type="button"
            className="text-button text-button--danger"
            onClick={() => {
              if (
                window.confirm(
                  `Möchtest du deinen Fortschritt im ${course.title} wirklich zurücksetzen? ` +
                    `${progress.completedCount} erledigte Schritte gehen dabei verloren.`,
                )
              ) {
                progress.reset();
              }
            }}
          >
            <Icon name="reset" />
            Fortschritt zurücksetzen
          </button>
        </footer>
      ) : null}
    </div>
  );
}
